import React, {
    FunctionComponent,
    useState,
    ChangeEvent,
    useEffect,
} from 'react'
import { useTranslation } from 'react-i18next'
import some from 'lodash/some'
import { Step3Props } from './types'
import { Step3ValueModel } from '../types'
import { validateStep3, validateStoreName } from '../Validation'
import {
    validateMunicipality,
    validateHasRvm,
    validateRvmEmail,
    validateVisitingAddressStreetAddress,
    validateVisitingAddressZipCode,
    validateVisitingAddressCity,
    validatePostalAddressStreetBox,
    validatePostalAddressZipCode,
    validatePostalAddressCity,
    validateSignatoryFirstName,
    validateSignatoryLastName,
    validateSignatoryEmail,
    validateInvoiceAddressStreetAddress,
    validateInvoiceAddressZipCode,
    validateInvoiceAddressCity,
    validateInvoiceAddressEmail,
    validateDeliveryAddressStreetAddress,
    validateDeliveryAddressCity,
    validateDeliveryAddressZipCode,
} from '../../shared/Validation';
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import { getAllErrors, removeAllErrorsForKey } from "@sharedComponents/PantaForm/utils";
import { InputSelectOption } from "@sharedComponents/InputSelect/types";
import InputText from "@sharedComponents/InputText";
import InputSelect from "@sharedComponents/InputSelect";
import { IMunicipality } from "@apps/Shared/Services/CompanyRegistrations/interfaces";
import InputToggleButton from "@sharedComponents/InputToggleButton";
import Panel from "@sharedComponents/Panel";
import { PanelType } from "@sharedComponents/Panel/Panel";
import InputCheckbox from "@sharedComponents/InputCheckbox";
import InputTextArea from "@sharedComponents/InputTextArea";
import Alert from "@sharedComponents/Alert";
import Button from "@sharedComponents/Button";
import {ButtonType} from '@sharedComponents/Button/types'
import Icon from "@sharedComponents/Icon";

const Step3: FunctionComponent<Step3Props> = props => {
    const { t } = useTranslation()

    const [formErrors, setFormErrors] = useState<FormErrorType[]>([])

    const allErrors = getAllErrors(formErrors)

    const [stepValues, setStepValues] = useState<Step3ValueModel>({
        signatory: {},
        invoiceAddress: {},
        visitingAddress: {},
        postalAddress: {},
        deliveryAddress: {},
    })

    const rehydrate = (values: Step3ValueModel) => {
        if (values) {
            setStepValues({
                ...stepValues,
                storeName: values.storeName || '',
                unitName: values.unitName || '',
                municipality: values.municipality,
                hasRvm: values.hasRvm || '',
                rvmEmail: values.rvmEmail || '',
                signatory: values.signatory || {},
                invoiceAddress: values.invoiceAddress || {},
                visitingAddress: values.visitingAddress || {},
                postalAddress: values.postalAddress || {},
                deliveryAddress: values.deliveryAddress || {},
                message: values.message || '',
            } as Step3ValueModel)
        }
    }

    useEffect(() => {
        rehydrate(props.values)
    }, [props.rehydrateFormKey])

    useEffect(() => {
        rehydrate(props.values)
    }, [])

    const onChangeStoreName = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({ ...stepValues, storeName: value })
        props.onChangeValues && props.onChangeValues({ storeName: value })
        setFormErrors(validateStoreName(value, formErrors))
    }

    const onChangeUnitName = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({ ...stepValues, unitName: value })
        props.onChangeValues && props.onChangeValues({ unitName: value })
    }

    const onChangeMunicipality = (option: InputSelectOption) => {
        const value = option
        setStepValues({ ...stepValues, municipality: value })
        props.onChangeValues && props.onChangeValues({ municipality: value })
        setFormErrors(validateMunicipality(value, formErrors))
    }

    const onChangeHasRvm = (value: string) => {
        let newStepValues = { ...stepValues, hasRvm: value }
        let formEdits: any = { hasRvm: value }
        let newFormErrors = validateHasRvm(value, formErrors)

        if (value === 'false') {
            // Cleanup dependent values
            newFormErrors = removeAllErrorsForKey(newFormErrors, 'rvmEmail')
            newFormErrors = removeAllErrorsForKey(
                newFormErrors,
                'signatory.firstName'
            )
            newFormErrors = removeAllErrorsForKey(
                newFormErrors,
                'signatory.lastName'
            )
            newFormErrors = removeAllErrorsForKey(
                newFormErrors,
                'signatory.email'
            )
            newFormErrors = removeAllErrorsForKey(
                newFormErrors,
                'invoiceAddress.streetAddress'
            )
            newFormErrors = removeAllErrorsForKey(
                newFormErrors,
                'invoiceAddress.zipCode'
            )
            newFormErrors = removeAllErrorsForKey(
                newFormErrors,
                'invoiceAddress.city'
            )
            newFormErrors = removeAllErrorsForKey(
                newFormErrors,
                'invoiceAddress.email'
            )
            newFormErrors = removeAllErrorsForKey(
                newFormErrors,
                'deliveryAddress.streetAddress'
            )
            newFormErrors = removeAllErrorsForKey(
                newFormErrors,
                'deliveryAddress.zipCode'
            )
            newFormErrors = removeAllErrorsForKey(
                newFormErrors,
                'deliveryAddress.city'
            )
            newStepValues = {
                ...newStepValues,
                rvmEmail: '',
                signatory: {},
                invoiceAddress: {},
                deliveryAddress: {},
            }
            formEdits = {
                ...formEdits,
                rvmEmail: '',
                signatory: {},
                invoiceAddress: {},
                deliveryAddress: {},
            }
        }
        props.onChangeValues && props.onChangeValues(formEdits)
        setFormErrors(newFormErrors)
        setStepValues(newStepValues)
    }

    const onChangeRvmEmail = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        setStepValues({ ...stepValues, rvmEmail: value })
        props.onChangeValues && props.onChangeValues({ rvmEmail: value })
        setFormErrors(validateRvmEmail(value, true, formErrors))
    }

    const onChangeSignatoryFirstName = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        setStepValues({
            ...stepValues,
            signatory: {
                ...stepValues.signatory,
                firstName: value,
            },
        })

        props.onChangeValues &&
            props.onChangeValues({
                signatory: {
                    ...stepValues.signatory,
                    firstName: value,
                },
            })

        setFormErrors(validateSignatoryFirstName(value, formErrors))
    }

    const onChangeSignatoryLastName = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        setStepValues({
            ...stepValues,
            signatory: {
                ...stepValues.signatory,
                lastName: value,
            },
        })

        props.onChangeValues &&
            props.onChangeValues({
                signatory: {
                    ...stepValues.signatory,
                    lastName: value,
                },
            })

        setFormErrors(validateSignatoryLastName(value, formErrors))
    }

    const onChangeSignatoryEmail = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        setStepValues({
            ...stepValues,
            signatory: {
                ...stepValues.signatory,
                email: value,
            },
        })

        props.onChangeValues &&
            props.onChangeValues({
                signatory: {
                    ...stepValues.signatory,
                    email: value,
                },
            })

        setFormErrors(validateSignatoryEmail(value, formErrors))
    }

    const onChangeSignatoryPhone = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        setStepValues({
            ...stepValues,
            signatory: {
                ...stepValues.signatory,
                phone: value,
            },
        })

        props.onChangeValues &&
            props.onChangeValues({
                signatory: {
                    ...stepValues.signatory,
                    phone: value,
                },
            })
    }

    const onChangeInvoiceAddressCoAddress = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value

        setStepValues({
            ...stepValues,
            invoiceAddress: {
                ...stepValues.invoiceAddress,
                coAddress: value,
            },
        })

        props.onChangeValues &&
            props.onChangeValues({
                invoiceAddress: {
                    ...stepValues.invoiceAddress,
                    coAddress: value,
                },
            })
    }

    const onChangeInvoiceAddressStreetAddress = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value

        setStepValues({
            ...stepValues,
            invoiceAddress: {
                ...stepValues.invoiceAddress,
                streetAddress: value,
            },
        })

        props.onChangeValues &&
            props.onChangeValues({
                invoiceAddress: {
                    ...stepValues.invoiceAddress,
                    streetAddress: value,
                },
            })
        setFormErrors(validateInvoiceAddressStreetAddress(value, formErrors))
    }

    const onChangeInvoiceAddressZipCode = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        let value = e.target.value
        if (value.length > 4) {
            value = value.trimEnd()
        }

        const invoiceAddress = {
            ...stepValues.invoiceAddress,
            zipCode: value,
        }

        setStepValues({
            ...stepValues,
            invoiceAddress,
        })

        setFormErrors(
            validateInvoiceAddressZipCode(
                value,
                true,
                formErrors
            )
        )

        props.onChangeValues && props.onChangeValues({ invoiceAddress })
    }

    const onChangeInvoiceAddressCity = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        const invoiceAddress = {
            ...stepValues.invoiceAddress,
            city: value,
        }

        setStepValues({
            ...stepValues,
            invoiceAddress,
        })

        props.onChangeValues && props.onChangeValues({ invoiceAddress })
        setFormErrors(validateInvoiceAddressCity(value, formErrors))
    }

    const onChangeInvoiceAddressEmail = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        setStepValues({
            ...stepValues,
            invoiceAddress: {
                ...stepValues.invoiceAddress,
                email: value,
            },
        })

        props.onChangeValues &&
            props.onChangeValues({
                invoiceAddress: {
                    ...stepValues.invoiceAddress,
                    email: value,
                },
            })

        setFormErrors(validateInvoiceAddressEmail(value, formErrors))
    }

    const onChangeVisitingAddressStreetAddress = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value

        setStepValues({
            ...stepValues,
            visitingAddress: {
                ...stepValues.visitingAddress,
                streetAddress: value,
            },
        })

        props.onChangeValues &&
            props.onChangeValues({
                visitingAddress: {
                    ...stepValues.visitingAddress,
                    streetAddress: value,
                },
            })

        setFormErrors(validateVisitingAddressStreetAddress(value, formErrors))
    }

    const onChangeVisitingAddressZipCode = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        let value = e.target.value
        if (value.length > 4) {
            value = value.trimEnd()
        }

        const visitingAddress = {
            ...stepValues.visitingAddress,
            zipCode: value,
        }

        setStepValues({
            ...stepValues,
            visitingAddress,
        })

        setFormErrors(
            validateVisitingAddressZipCode(
                value,
                true,
                formErrors
            )
        )

        props.onChangeValues && props.onChangeValues({ visitingAddress })
    }

    const onChangeVisitingAddressCity = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        const visitingAddress = {
            ...stepValues.visitingAddress,
            city: value,
        }

        setStepValues({
            ...stepValues,
            visitingAddress,
        })

        props.onChangeValues && props.onChangeValues({ visitingAddress })
        setFormErrors(validateVisitingAddressCity(value, formErrors))
    }

    const onChangeVisitingAddressIsAlsoPostalAddress = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.checked

        const visitingAddress = {
            ...stepValues.visitingAddress,
            isAlsoPostalAddress: value,
        }

        const postalAddress = value === true ? {} : stepValues.postalAddress
        const deliveryAddress = value === true ? {} : stepValues.deliveryAddress

        setStepValues({
            ...stepValues,
            visitingAddress,
            postalAddress,
            deliveryAddress,
        })

        props.onChangeValues &&
            props.onChangeValues({
                visitingAddress,
                postalAddress,
                deliveryAddress,
            })

        if (value === true) {
            let errors = removeAllErrorsForKey(
                formErrors,
                'postalAddress.streetBox'
            )
            errors = removeAllErrorsForKey(errors, 'postalAddress.zipCode')
            errors = removeAllErrorsForKey(errors, 'postalAddress.city')
            errors = removeAllErrorsForKey(
                errors,
                'deliveryAddress.streetAddress'
            )
            errors = removeAllErrorsForKey(errors, 'deliveryAddress.zipCode')
            errors = removeAllErrorsForKey(errors, 'deliveryAddress.city')

            setFormErrors(errors)
        }
    }

    const onChangePostalAddressStreetBox = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value

        const postalAddress = {
            ...stepValues.postalAddress,
            streetBox: value,
        }

        setStepValues({ ...stepValues, postalAddress })
        props.onChangeValues && props.onChangeValues({ postalAddress })
        setFormErrors(
            validatePostalAddressStreetBox(
                value,
                !!stepValues.visitingAddress.isAlsoPostalAddress,
                formErrors
            )
        )
    }

    const onChangePostalAddressZipCode = (e: ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value
        if (value.length > 4) {
            value = value.trimEnd()
        }

        const postalAddress = {
            ...stepValues.postalAddress,
            zipCode: value,
        }

        setStepValues({ ...stepValues, postalAddress })
        props.onChangeValues && props.onChangeValues({ postalAddress })

        setFormErrors(
            validatePostalAddressZipCode(
                value,
                !stepValues.visitingAddress.isAlsoPostalAddress,
                true,
                formErrors
            )
        )
    }

    const onChangePostalAddressCity = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        const postalAddress = {
            ...stepValues.postalAddress,
            city: value,
        }

        setStepValues({ ...stepValues, postalAddress })
        props.onChangeValues && props.onChangeValues({ postalAddress })
        setFormErrors(
            validatePostalAddressCity(
                value,
                !!stepValues.visitingAddress.isAlsoPostalAddress,
                formErrors
            )
        )
    }

    const onChangeDeliveryAddressStreetAddress = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value

        setStepValues({
            ...stepValues,
            deliveryAddress: {
                ...stepValues.deliveryAddress,
                streetAddress: value,
            },
        })

        props.onChangeValues &&
            props.onChangeValues({
                deliveryAddress: {
                    ...stepValues.deliveryAddress,
                    streetAddress: value,
                },
            })

        setFormErrors(
            validateDeliveryAddressStreetAddress(
                value,
                !!stepValues.visitingAddress.isAlsoPostalAddress,
                formErrors
            )
        )
    }

    const onChangeDeliveryAddressZipCode = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        let value = e.target.value
        if (value.length > 4) {
            value = value.trimEnd()
        }

        const deliveryAddress = {
            ...stepValues.deliveryAddress,
            zipCode: value,
        }

        setStepValues({
            ...stepValues,
            deliveryAddress,
        })

        props.onChangeValues && props.onChangeValues({ deliveryAddress })

        setFormErrors(
            validateDeliveryAddressZipCode(
                value,
                !!stepValues.visitingAddress.isAlsoPostalAddress,
                true,
                formErrors
            )
        )
    }

    const onChangeDeliveryAddressCity = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        const deliveryAddress = {
            ...stepValues.deliveryAddress,
            city: value,
        }

        setStepValues({
            ...stepValues,
            deliveryAddress,
        })

        props.onChangeValues && props.onChangeValues({ deliveryAddress })
        setFormErrors(
            validateDeliveryAddressCity(
                value,
                !stepValues.visitingAddress.isAlsoPostalAddress,
                formErrors
            )
        )
    }

    const onChangeMessage = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value

        setStepValues({
            ...stepValues,
            message: value,
        })

        props.onChangeValues && props.onChangeValues({ message: value })
    }

    const nextStep = () => {
        validateStep3(stepValues, formErrors).then(
            (errors: FormErrorType[]) => {
                if (errors && errors.length > 0) {
                    setFormErrors(errors)
                } else {
                    setFormErrors([])
                    props.onChangeValues && props.onChangeValues({})
                    props.nextStep && props.nextStep()
                }
            }
        )
    }

    const prevStep = () => {
        props.prevStep && props.prevStep()
    }

    const onCancel = () => {
        setFormErrors([])

        props.onCancel && props.onCancel()
    }

    const uniqueErrors = (key: string) => {
        const errors = formErrors.find(err => err.key === key);
        const uniqueErr = errors ? [...new Set(errors.messages)] : [];

        return uniqueErr.length > 0 ? uniqueErr.join(', ') : '';
    }

    return (
        <div className='form-step-wrapper form-step-wrapper--wide'>
            <h3>{t('companyRegistration.customer.store.stepLabel.step3')}</h3>
            <p className="required-asterisk mt-3">{t('global.requiredAsterisk')}</p>
            {props.cmsContent ? (
                <div className='form-step-wrapper__content'>
                    {props.cmsContent}
                </div>
            ) : (
                ''
            )}
            <div className='fields-wrapper'>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="store-name"
                        label={t(
                            'companyRegistration.customer.store.storeName'
                        )}
                        value={stepValues.storeName || ''}
                        hasError={!!some(formErrors, ['key', 'storeName'])}
                        required={true}
                        maxLength={50}
                        onChange={onChangeStoreName}
                        autoComplete="organization"
                        errorText={uniqueErrors('storeName')}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="unit-name"
                        label={t('companyRegistration.customer.unitName')}
                        value={stepValues.unitName || ''}
                        maxLength={50}
                        hasError={!!some(formErrors, ['key', 'unitName'])}
                        onChange={onChangeUnitName}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputSelect
                        id="municipality"
                        emptyMessage={t('global.choose')}
                        hideLabel={false}
                        label={t('companyRegistration.customer.municipality')}
                        value={stepValues.municipality}
                        onChange={onChangeMunicipality}
                        hasError={!!some(formErrors, ['key', 'municipality'])}
                        options={props.municipalities.map(
                            (municipality: IMunicipality) => {
                                return {
                                    label: municipality.description,
                                    value: municipality.code + '',
                                } as InputSelectOption
                            }
                        )}
                        required={true}
                        disabled={props.municipalities.length === 0}
                        errorText={uniqueErrors('municipality')}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputToggleButton
                        id="has-rvm"
                        className="toggle-field"
                        label={t('companyRegistration.customer.hasRvm')}
                        options={[
                            {
                                value: 'true',
                                label: t('companyRegistration.yes'),
                            },
                            {
                                value: 'false',
                                label: t('companyRegistration.no'),
                            },
                        ]}
                        value={stepValues.hasRvm || ''}
                        hasError={!!some(formErrors, ['key', 'hasRvm'])}
                        required={true}
                        onSelect={onChangeHasRvm}
                        errorText={uniqueErrors('hasRvm')}
                    />
                </div>
                {stepValues.hasRvm === 'true' && (
                    <>
                        <div className='fields-wrapper__field'>
                            <InputText
                                id="rvm-email"
                                label={t(
                                    'companyRegistration.customer.rvmEmail'
                                )}
                                value={stepValues.rvmEmail || ''}
                                hasError={
                                    !!some(formErrors, ['key', 'rvmEmail'])
                                }
                                required={true}
                                onChange={onChangeRvmEmail}
                                type="email"
                                autoComplete="email"
                                maxLength={250}
                                errorText={uniqueErrors('rvmEmail')}
                            />
                        </div>
                        <>
                            <h5 className='fields-wrapper__title'>
                                {t('companyRegistration.customer.signatory')}
                            </h5>
                            <div className='fields-wrapper__field'>
                                <Panel type={PanelType.Info} compact={true}>
                                    <span className='field-info'>
                                        {t(
                                            'companyRegistration.customer.signatoryInfo'
                                        )}
                                    </span>
                                </Panel>
                            </div>
                            <div className='fields-wrapper__field'>
                                <InputText
                                    id="signatory-first-name"
                                    label={t('companyRegistration.firstName')}
                                    value={
                                        stepValues.signatory?.firstName || ''
                                    }
                                    hasError={
                                        !!some(formErrors, [
                                            'key',
                                            'signatory.firstName',
                                        ])
                                    }
                                    maxLength={50}
                                    onChange={onChangeSignatoryFirstName}
                                    required={true}
                                    autoComplete="given-name"
                                    errorText={uniqueErrors('signatory.firstName')}
                                />
                            </div>
                            <div className='fields-wrapper__field'>
                                <InputText
                                    id="signatory-last-name"
                                    label={t('companyRegistration.lastName')}
                                    value={stepValues.signatory?.lastName || ''}
                                    hasError={
                                        !!some(formErrors, [
                                            'key',
                                            'signatory.lastName',
                                        ])
                                    }
                                    maxLength={50}
                                    onChange={onChangeSignatoryLastName}
                                    required={true}
                                    autoComplete="family-name"
                                    errorText={uniqueErrors('signatory.lastName')}
                                />
                            </div>
                            <div className='fields-wrapper__field'>
                                <InputText
                                    id="signatory-email"
                                    label={t('companyRegistration.email')}
                                    value={stepValues.signatory?.email || ''}
                                    hasError={
                                        !!some(formErrors, [
                                            'key',
                                            'signatory.email',
                                        ])
                                    }
                                    onChange={onChangeSignatoryEmail}
                                    required={true}
                                    type="email"
                                    autoComplete="email"
                                    errorText={uniqueErrors('signatory.email')}
                                />
                            </div>
                            <div className='fields-wrapper__field'>
                                <InputText
                                    id="signatory-phone"
                                    label={t('companyRegistration.phoneNumber')}
                                    value={stepValues.signatory?.phone || ''}
                                    hasError={
                                        !!some(formErrors, [
                                            'key',
                                            'signatory.phone',
                                        ])
                                    }
                                    onChange={onChangeSignatoryPhone}
                                    maxLength={30}
                                    required={false}
                                    autoComplete="tel"
                                />
                            </div>
                        </>
                        <>
                            <h5 className='fields-wrapper__title'>
                                {t(
                                    'companyRegistration.customer.invoiceAddress'
                                )}
                            </h5>
                            <div className='fields-wrapper__field'>
                                <Panel type={PanelType.Info} compact={true}>
                                    <span className='field-info'>
                                        {t(
                                            'companyRegistration.customer.invoiceAddressInfo'
                                        )}
                                    </span>
                                </Panel>
                            </div>
                            <div className='fields-wrapper__field'>
                                <InputText
                                    id="co-address-street-address"
                                    label={t(
                                        'companyRegistration.customer.careOf'
                                    )}
                                    value={
                                        stepValues.invoiceAddress?.coAddress ||
                                        ''
                                    }
                                    hasError={
                                        !!some(formErrors, [
                                            'key',
                                            'invoiceAddress.coAddress',
                                        ])
                                    }
                                    maxLength={100}
                                    onChange={onChangeInvoiceAddressCoAddress}
                                    required={false}
                                    autoComplete="street-address"
                                />
                            </div>
                            <div className='fields-wrapper__field'>
                                <InputText
                                    id="invoice-address-street-address"
                                    label={t(
                                        'companyRegistration.streetAddress'
                                    )}
                                    value={
                                        stepValues.invoiceAddress
                                            ?.streetAddress || ''
                                    }
                                    hasError={
                                        !!some(formErrors, [
                                            'key',
                                            'invoiceAddress.streetAddress',
                                        ])
                                    }
                                    onChange={
                                        onChangeInvoiceAddressStreetAddress
                                    }
                                    maxLength={100}
                                    required={true}
                                    autoComplete="street-address"
                                    errorText={uniqueErrors('invoiceAddress.streetAddress')}
                                />
                            </div>
                            <div className='fields-wrapper__field'>
                                <InputText
                                    id="invoice-address-zip-code"
                                    label={t('companyRegistration.zipCode')}
                                    value={
                                        stepValues.invoiceAddress?.zipCode || ''
                                    }
                                    hasError={
                                        !!some(formErrors, [
                                            'key',
                                            'invoiceAddress.zipCode',
                                        ])
                                    }
                                    onChange={onChangeInvoiceAddressZipCode}
                                    required={true}
                                    autoComplete="postal-code"
                                    errorText={uniqueErrors('invoiceAddress.zipCode')}
                                />
                            </div>
                            <div className='fields-wrapper__field'>
                                <InputText
                                    id="invoice-address-city"
                                    label={t('companyRegistration.city')}
                                    value={
                                        stepValues.invoiceAddress?.city || ''
                                    }
                                    hasError={
                                        !!some(formErrors, [
                                            'key',
                                            'invoiceAddress.city',
                                        ])
                                    }
                                    maxLength={50}
                                    onChange={onChangeInvoiceAddressCity}
                                    required={true}
                                    autoComplete="street-address"
                                    errorText={uniqueErrors('invoiceAddress.city')}
                                />
                            </div>
                            <div className='fields-wrapper__field'>
                                <InputText
                                    id="invoice-address-email"
                                    label={t(
                                        'companyRegistration.emailInvoice'
                                    )}
                                    value={
                                        stepValues.invoiceAddress?.email || ''
                                    }
                                    hasError={
                                        !!some(formErrors, [
                                            'key',
                                            'invoiceAddress.email',
                                        ])
                                    }
                                    onChange={onChangeInvoiceAddressEmail}
                                    required={true}
                                    type="email"
                                    autoComplete="email"
                                    errorText={uniqueErrors('invoiceAddress.email')}
                                />
                            </div>
                        </>
                    </>
                )}
                {
                    <>
                        <h5 className='fields-wrapper__title'>
                            {t('companyRegistration.customer.visitingAddress')}
                        </h5>
                        <div className='fields-wrapper__field'>
                            <InputText
                                id="visiting-address-street-address"
                                label={t('companyRegistration.streetAddress')}
                                value={
                                    stepValues.visitingAddress.streetAddress ||
                                    ''
                                }
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'visitingAddress.streetAddress',
                                    ])
                                }
                                maxLength={100}
                                onChange={onChangeVisitingAddressStreetAddress}
                                required={true}
                                autoComplete="street-address"
                                errorText={uniqueErrors('visitingAddress.streetAddress')}
                            />
                        </div>
                        <div className='fields-wrapper__field'>
                            <InputText
                                id="visiting-address-zip-code"
                                label={t('companyRegistration.zipCode')}
                                value={stepValues.visitingAddress.zipCode || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'visitingAddress.zipCode',
                                    ])
                                }
                                onChange={onChangeVisitingAddressZipCode}
                                required={true}
                                autoComplete="postal-code"
                                errorText={uniqueErrors('visitingAddress.zipCode')}
                            />
                        </div>
                        <div className='fields-wrapper__field'>
                            <InputText
                                id="visiting-address-city"
                                label={t('companyRegistration.city')}
                                value={stepValues.visitingAddress.city || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'visitingAddress.city',
                                    ])
                                }
                                onChange={onChangeVisitingAddressCity}
                                required={true}
                                maxLength={50}
                                autoComplete="street-address"
                                errorText={uniqueErrors('visitingAddress.city')}
                            />
                        </div>
                        <div className='fields-wrapper__field'>
                            <InputCheckbox
                                id="visiting-address-is-also-postal-address"
                                label={t(
                                    'companyRegistration.customer.visitingAddressIsAlsoPostalAddress'
                                )}
                                onChange={
                                    onChangeVisitingAddressIsAlsoPostalAddress
                                }
                                checked={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress || false
                                }
                            />
                        </div>
                    </>
                }
                {
                    <>
                        <h5 className={`fields-wrapper__title ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__title--disabled' : ''}`}>
                            {t('companyRegistration.customer.postalAddress')}
                        </h5>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <InputText
                                id="postal-address-street-box"
                                label={t(
                                    'companyRegistration.customer.streetBox'
                                )}
                                value={stepValues.postalAddress.streetBox || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'postalAddress.streetBox',
                                    ])
                                }
                                onChange={onChangePostalAddressStreetBox}
                                required={true}
                                disabled={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress
                                }
                                maxLength={100}
                                autoComplete="address-line1"
                                errorText={uniqueErrors('postalAddress.streetBox')}
                            />
                        </div>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <InputText
                                id="postal-address-zip-code"
                                label={t('companyRegistration.zipCode')}
                                value={stepValues.postalAddress.zipCode || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'postalAddress.zipCode',
                                    ])
                                }
                                onChange={onChangePostalAddressZipCode}
                                required={true}
                                disabled={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress
                                }
                                autoComplete="postal-code"
                                errorText={uniqueErrors('postalAddress.zipCode')}
                            />
                        </div>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <InputText
                                id="postal-address-city"
                                label={t('companyRegistration.city')}
                                value={stepValues.postalAddress.city || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'postalAddress.city',
                                    ])
                                }
                                onChange={onChangePostalAddressCity}
                                required={true}
                                disabled={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress
                                }
                                maxLength={50}
                                autoComplete="city"
                                errorText={uniqueErrors('postalAddress.city')}
                            />
                        </div>
                    </>
                }
                {stepValues.hasRvm === 'true' && (
                    <>
                        <h5 className={`fields-wrapper__title ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__title--disabled' : ''}`}>
                            {t('companyRegistration.customer.deliveryAddress')}
                        </h5>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <Panel type={PanelType.Info} compact={true}>
                                <span className='field-info'>
                                    {t(
                                        'companyRegistration.customer.deliveryAddressInfo'
                                    )}
                                </span>
                            </Panel>
                        </div>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <InputText
                                id="delivery-address-street-address"
                                label={t('companyRegistration.streetAddress')}
                                value={
                                    stepValues.deliveryAddress?.streetAddress ||
                                    ''
                                }
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'deliveryAddress.streetAddress',
                                    ])
                                }
                                onChange={onChangeDeliveryAddressStreetAddress}
                                required={true}
                                disabled={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress
                                }
                                maxLength={100}
                                autoComplete="street-address"
                                errorText={uniqueErrors('deliveryAddress.streetAddress')}
                            />
                        </div>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <InputText
                                id="delivery-address-zip-code"
                                label={t('companyRegistration.zipCode')}
                                value={
                                    stepValues.deliveryAddress?.zipCode || ''
                                }
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'deliveryAddress.zipCode',
                                    ])
                                }
                                onChange={onChangeDeliveryAddressZipCode}
                                required={true}
                                disabled={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress
                                }
                                autoComplete="postal-code"
                                errorText={uniqueErrors('deliveryAddress.zipCode')}
                            />
                        </div>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <InputText
                                id="delivery-address-city"
                                label={t('companyRegistration.city')}
                                value={stepValues.deliveryAddress?.city || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'deliveryAddress.city',
                                    ])
                                }
                                onChange={onChangeDeliveryAddressCity}
                                required={true}
                                disabled={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress
                                }
                                maxLength={50}
                                autoComplete="street-address"
                                errorText={uniqueErrors('deliveryAddress.city')}
                            />
                        </div>
                    </>
                )}
                {
                    <>
                        <h5 className='fields-wrapper__title'>
                            {t('companyRegistration.customer.messageForRp')}
                        </h5>
                        <div className='fields-wrapper__field'>
                            <InputTextArea
                                id="message-to-rp"
                                label={t(
                                    'companyRegistration.customer.message'
                                )}
                                value={stepValues.message || ''}
                                hasError={
                                    !!some(formErrors, ['key', 'message'])
                                }
                                maxLength={512}
                                onChange={onChangeMessage}
                            />
                        </div>
                    </>
                }
            </div>
            {allErrors.length > 0 && (
                <div className='form-step-wrapper__error'>
                    <Alert
                        type="danger"
                        text={t(
                            'companyRegistration.companyInfoContainsErrors'
                        )}
                        subtext={allErrors}
                        compact={false}
                    />
                </div>
            )}
            {props.unexpectedErrors.length > 0 && (
                <Alert
                    type="danger"
                    text={''}
                    subtext={props.unexpectedErrors}
                    compact={true}
                />
            )}
            <div className='buttons-wrapper'>
                <Button
                    type={ButtonType.PRIMARY}
                    className="next-button"
                    text={
                        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--next'>
                            <span>{t('button.next')}</span>
                            <Icon iconName="arrow-right" />
                        </span>
                    }
                    onClick={nextStep}
                />
                <Button
                    type={ButtonType.PRIMARY}
                    className="prev-button"
                    text={
                        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--prev'>
                            <Icon iconName="arrow-left" />
                            <span>{t('button.previous')}</span>
                        </span>
                    }
                    onClick={prevStep}
                />
                <Button
                    type={ButtonType.SECONDARY}
                    className="cancel-button"
                    text={
                        <span className='buttons-wrapper__button-text'>
                            <Icon iconName="close" />
                            <span>{t('button.cancel')}</span>
                        </span>
                    }
                    onClick={onCancel}
                />
            </div>
        </div>
    )
}

export default Step3